/* global SideDrawer */

if (!customElements.get('facet-filters')) {
  class FacetFilters extends SideDrawer {
    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.historyChangeHandler);

      if (this.breakpointChangeHandler) {
        window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }
    }

    init() {
      this.filteringEnabled = this.dataset.filtering === 'true';
      this.sortingEnabled = this.dataset.sorting === 'true';
      this.form = document.getElementById('facets');
      this.results = document.getElementById('filter-results');
      this.expanded = [];
      this.filterChangeTimeout = null;

      this.handleBreakpointChange();
      this.addElements();
      this.addListeners();

      this.historyChangeHandler = this.historyChangeHandler || this.handleHistoryChange.bind(this);
      window.addEventListener('popstate', this.historyChangeHandler);

      // Stamp the initial history entry: handleHistoryChange ignores null states,
      // so without this, navigating Back to the entry that existed at page load
      // reverts the URL but leaves the filtered results on screen.
      if (window.history.state === null || window.history.state.searchParams === undefined) {
        window.history.replaceState(
          { ...window.history.state, searchParams: window.location.search.replace(/^\?/, '') },
          ''
        );
      }
    }

    addElements() {
      if (this.filteringEnabled) {
        this.filters = this.querySelector('.facets__filters');
        this.activeFilters = this.querySelector('.facets__active-filters');
        this.activeFiltersList = this.querySelector('.active-filters');
        this.activeFiltersHeader = this.querySelector('.active-filters-header');
        this.footer = this.querySelector('.facets__footer');
      }

      if (this.sortingEnabled) {
        this.mobileSortByOptions = this.querySelectorAll('.js-drawer-sort-by');
        this.desktopSortBy = document.querySelector('.products-toolbar__sort');
      }
    }

    addListeners() {
      // Cache the bound handlers: addListeners() runs again after every AJAX update,
      // and this.desktopSortBy survives those updates — fresh .bind() calls would
      // stack duplicate listeners on it.
      this.filterChangeHandler = this.filterChangeHandler || this.handleFilterChange.bind(this);

      if (this.filteringEnabled) {
        this.breakpointChangeHandler = this.breakpointChangeHandler
          || this.handleBreakpointChange.bind(this);
        this.filtersClickHandler = this.filtersClickHandler || this.handleFiltersClick.bind(this);
        this.activeFiltersClickHandler = this.activeFiltersClickHandler
          || this.handleActiveFiltersClick.bind(this);
        this.filters.addEventListener('click', this.filtersClickHandler);
        this.filters.addEventListener('input', this.filterChangeHandler);
        this.filters.addEventListener('change', this.filterChangeHandler);
        this.activeFilters.addEventListener('click', this.activeFiltersClickHandler);
        window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }

      if (this.sortingEnabled) {
        this.desktopSortBy.addEventListener('change', this.filterChangeHandler);
      }
    }

    /**
     * Handles viewport breakpoint changes.
     */
    handleBreakpointChange() {
      if (theme.mediaMatches.lg) {
        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
        this.removeAttribute('aria-modal');
        this.removeAttribute('role');
      } else {
        this.close();
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('aria-hidden', 'true');
        this.hidden = false;
      }
    }

    /**
     * Handles 'input' events on the filters and 'change' events on the sort by dropdown.
     * @param {object} evt - Event object.
     */
    handleFilterChange(evt) {
      const inMetafieldRange = typeof evt.target.closest === 'function' && evt.target.closest('metafield-range') !== null;

      // Metafield ranges apply on commit ('change') only: their 'input' events are
      // slider-drag/typing previews handled inside <metafield-range> and must never
      // trigger a reload (the AJAX re-render would destroy the in-progress edit).
      // They do postpone an apply armed by an earlier commit, for the same reason.
      if (evt.type === 'input' && inMetafieldRange) {
        if (this.filterChangeTimeout) this.scheduleApplyFromForm(this.lastFilterChangeEvt);
        return;
      }

      // Only allow price/metafield-range 'change' events
      if (evt.type === 'change' && !(inMetafieldRange || evt.target.id?.includes('price-range') || evt.target.id?.includes('sort-by'))) return;

      // Dont reload when typing a price
      if (evt.target.id?.includes('price-range') && evt.constructor.name === 'InputEvent') return;

      this.lastFilterChangeEvt = evt;
      this.scheduleApplyFromForm(evt);
    }

    /**
     * (Re)arms the debounced form serialization + filter apply.
     * @param {object} evt - Event object that triggered the (re)scheduling.
     */
    scheduleApplyFromForm(evt) {
      const timeoutDelay = 500;

      clearTimeout(this.filterChangeTimeout);

      this.filterChangeTimeout = setTimeout(() => {
        this.filterChangeTimeout = null;
        const formData = new FormData(this.form);
        const searchParams = new URLSearchParams(formData);
        const emptyParams = [];

        if (this.sortingEnabled) {
          let currentSortBy = searchParams.get('sort_by');

          // Keep the mobile facets form sync'd with the desktop sort by dropdown
          if (evt.target.tagName === 'CUSTOM-SELECT') {
            this.mobileSortByOptions.forEach((option) => {
              option.checked = option.value === evt.detail.selectedValue;
              currentSortBy = evt.detail.selectedValue;
            });
          }

          // Set the 'sort_by' parameter. currentSortBy is null when no sort radio is
          // rendered/checked (e.g. the active sort option is hidden by settings) —
          // setting it would serialize the literal string "null" into the URL.
          if (currentSortBy) {
            searchParams.set('sort_by', currentSortBy);
          } else {
            searchParams.delete('sort_by');
          }
        }

        // Get empty parameters.
        searchParams.forEach((value, key) => {
          if (!value) emptyParams.push(key);
        });

        // Remove empty parameters.
        emptyParams.forEach((key) => {
          searchParams.delete(key);
        });

        this.applyFilters(searchParams.toString(), evt);
      }, timeoutDelay);
    }

    /**
     * Handles 'click' events on the filters.
     * @param {object} evt - Event object.
     */
    handleFiltersClick(evt) {
      const { target } = evt;

      // Filter 'clear' button clicked.
      if (target.matches('.js-clear-filter')) {
        evt.preventDefault();
        this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
      }

      // Filter 'show more' button clicked.
      if (target.matches('.js-show-more')) {
        const filter = target.closest('.filter');
        target.remove();

        filter.querySelectorAll('li').forEach((el) => {
          el.classList.remove('js-hidden');
        });

        if (!this.expanded.includes(filter.id)) {
          this.expanded.push(filter.id);
        }
      }
    }

    /**
     * Handles 'click' events on the active filters.
     * @param {object} evt - Event object.
     */
    handleActiveFiltersClick(evt) {
      if (evt.target.tagName !== 'A') return;
      evt.preventDefault();
      this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
    }

    /**
     * Handles history changes (e.g. back button clicked).
     * @param {object} evt - Event object.
     */
    handleHistoryChange(evt) {
      if (evt.state !== null) {
        let searchParams = '';

        if (evt.state && evt.state.searchParams) {
          ({ searchParams } = evt.state);
        }

        this.applyFilters(searchParams, null, false);
      }
    }

    /**
     * Returns the actual desktop sidebar state used by the grid geometry.
     * The column is authoritative because mobile drawer interactions can leave
     * the toolbar button active while the desktop sidebar remains hidden.
     * @returns {boolean|null} Whether desktop filters are open, if applicable.
     */
    getDesktopFiltersOpen() {
      const filtersColumn = document.querySelector('.main-products-grid__filters');
      if (filtersColumn) return !filtersColumn.classList.contains('lg:js-hidden');

      const filterToggle = document.querySelector('.js-toggle-filters');
      return filterToggle ? filterToggle.classList.contains('is-active') : null;
    }

    /**
     * Fetches the filtered/sorted page data and updates the current page.
     * @param {string} searchParams - Filter/sort search parameters.
     * @param {object} evt - Event object.
     * @param {boolean} [updateUrl=true] - Update url with the selected options.
     */
    async applyFilters(searchParams, evt, updateUrl = true) {
      let requestController;
      let customPagination;
      let closeBtn;
      try {
        // A direct apply (chip click, clear-all, history) supersedes any pending
        // debounced apply — a stale timer would re-serialize the pre-clear form
        // state and resurrect the filters the user just removed.
        clearTimeout(this.filterChangeTimeout);
        this.filterChangeTimeout = null;

        // Preserve the current element focus
        const activeElementId = document.activeElement.id;

        // Cancel the previous facet request before changing any shared UI.
        if (this.applyFiltersFetchAbortController) {
          this.applyFiltersFetchAbortController.abort();
        }
        requestController = new AbortController();
        this.applyFiltersFetchAbortController = requestController;

        // Disable infinite scrolling.
        customPagination = document.querySelector('custom-pagination');
        if (customPagination) {
          customPagination.dataset.pauseInfiniteScroll = 'true';
          // `pause` empêche seulement un prochain départ. Une page déjà en vol
          // doit aussi être interrompue pour ne jamais écrire après le filtre.
          customPagination.controller?.abort();
        }

        // Set loading state.
        this.results.classList.add('is-loading');

        // Disable "Show X results" button until submission is complete.
        closeBtn = this.querySelector('.js-close-drawer-mob');
        closeBtn.ariaDisabled = 'true';
        closeBtn.classList.add('is-loading');

        // Use Section Rendering API for the request, if possible.
        let fetchUrl = `${window.location.pathname}?${searchParams}`;
        if (this.form.dataset.filterSectionId) {
          fetchUrl += `&section_id=${this.form.dataset.filterSectionId}`;
        }

        // Fetch filtered products markup.
        const response = await fetch(fetchUrl, {
          method: 'GET',
          signal: requestController.signal
        });

        if (!response.ok) throw new Error(response.status);
        if (response.ok) {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();
          const nextResults = tmpl.content.getElementById('filter-results');
          if (!nextResults) throw new Error('Missing filter results.');

          await window.dgPrepareDynamicProductContent?.(nextResults);
          if (requestController.signal.aborted) return;

          // Restore UI state.
          this.form.querySelectorAll('details-disclosure > details').forEach((existingFilter) => {
            const target = tmpl.content.getElementById(existingFilter.id);
            if (target) {
              target.open = existingFilter.open;
            }
          });
          tmpl.content.querySelectorAll('#facets details-disclosure > details').forEach((newFilter) => {
            if (this.expanded.includes(newFilter.id)) {
              const hiddenElements = newFilter.querySelectorAll('.js-hidden');
              hiddenElements.forEach((listItem) => {
                listItem.classList.remove('js-hidden');
              });
              newFilter.querySelector('.filter__more')?.remove();
            }
          });

          // Update the filters.
          this.form.innerHTML = tmpl.content.getElementById('facets').innerHTML;

          // Update the label of the mobile filter button
          closeBtn.innerText = tmpl.content.querySelector('.js-close-drawer-mob').innerText;

          // Preserve the exact CSS class and the sidebar state represented by it.
          // If the current response is empty there is no <ul>, so keep the last
          // known pair until products return.
          const currentResultsUl = this.results.querySelector('ul');
          const filtersOpen = this.getDesktopFiltersOpen();
          if (currentResultsUl) {
            this.currentResultsClass = currentResultsUl.getAttribute('class');
            this.currentResultsFiltersOpen = filtersOpen;
          }

          // Update the results.
          this.results.innerHTML = tmpl.content.getElementById('filter-results').innerHTML;
          window.dgActivateDynamicProductContent?.(this.results);

          // Keep the exact existing class whenever it still represents the
          // current sidebar state. After an empty state (or a toggle while
          // empty), derive the matching open/closed class from the new grid.
          const newResultsUl = this.results.querySelector('ul');
          if (newResultsUl) {
            let resultsClass = this.currentResultsClass;
            if (filtersOpen !== null && this.currentResultsFiltersOpen !== filtersOpen) {
              resultsClass = filtersOpen
                ? newResultsUl.dataset.filtersOpenClasses
                : newResultsUl.dataset.filtersClosedClasses;
            }
            if (!resultsClass && filtersOpen !== null) {
              resultsClass = filtersOpen
                ? newResultsUl.dataset.filtersOpenClasses
                : newResultsUl.dataset.filtersClosedClasses;
            }
            if (resultsClass) {
              newResultsUl.setAttribute('class', resultsClass);
              this.currentResultsClass = resultsClass;
              this.currentResultsFiltersOpen = filtersOpen;
            }
          }

          // Reinitialize re-rendered components.
          this.addElements();
          this.addListeners();

          // Reinitialize any custom pagination
          if (customPagination && customPagination.reload) customPagination.reload();

          // Update the URL.
          if (updateUrl) FacetFilters.updateURL(searchParams);

          // Scroll to the top of the results if needed
          if (this.results.getBoundingClientRect().top < 0) {
            // If the header is sticky, compensate for it when scrolling to elements
            let headerHeight = 0;
            if (document.querySelector('store-header[data-is-sticky="true"]')) {
              headerHeight = Number.parseInt(
                getComputedStyle(this.parentElement)
                  .getPropertyValue('--header-height')
                  .replace('px', ''),
                10
              ) || 0;
            }
            window.scrollTo({
              top: this.results.getBoundingClientRect().top + window.scrollY - headerHeight - 45,
              behavior: 'smooth'
            });
          }

          // Enable the "Show X results" button
          closeBtn.classList.remove('is-loading');
          closeBtn.removeAttribute('aria-disabled');

          // Renable infinite scroll
          if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'false';

          // Focus on the element with the same ID in the new HTML
          if (activeElementId) document.getElementById(activeElementId)?.focus();

          // Broadcast the update for anything else to hook into
          document.dispatchEvent(new CustomEvent('on:facet-filters:updated', { bubbles: true }));
        }
      } catch (error) {
        if (error?.name !== 'AbortError') console.warn(error); // eslint-disable-line
      } finally {
        if (this.applyFiltersFetchAbortController === requestController) {
          this.applyFiltersFetchAbortController = null;
          this.results.classList.remove('is-loading');
          closeBtn?.classList.remove('is-loading');
          closeBtn?.removeAttribute('aria-disabled');
          if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'false';
        }
      }
    }

    /**
     * Updates the url with the current filter/sort parameters.
     * @param {string} searchParams - Filter/sort parameters.
     */
    static updateURL(searchParams) {
      window.history.pushState(
        { searchParams },
        '',
        `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
      );
    }
  }

  customElements.define('facet-filters', FacetFilters);
}
